import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { routeGenerator } from 'routes';
import { getIntersectionList, getSectionList, saveIntersection } from 'Root/http';
import { isSupervisorUser } from 'Auth/redux/auth.redux';
import { getLocationQuery } from 'shared/utils/url';

import Sidebar from './Sidebar/Sidebar';

import Roles from './Roles/Roles';
import Users from './Users/Users';
import s from './style/Sections.pcss';
import Loader from '../../../shared/components/Loader/Loader';


@connect(
  state => ({
    isSupervisor: isSupervisorUser(state),
  }),
)
export default class Sections extends React.Component {
  static propTypes = {
    match: PropTypes.shape().isRequired,
    history: PropTypes.shape().isRequired,
    isSupervisor: PropTypes.bool.isRequired,
  };


  state = {
    loading: false,
    sections: [],
    roles: [],
    users: [],
    totalUsers: 0,
    page: 0,
    search: '',
  };

  componentDidMount() {
    this.setState({ dataLoading: true }, () => {
      this.loadSectionsRoles();
    });
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;
    const needUpdate = (prevProps.match.params.sectionId !== match.params.sectionId) ||
      (prevProps.match.params.roleId !== match.params.roleId);

    if (needUpdate) {
      const roles = this.getRoles(this.state.sections);
      this.setState({ loading: true }, () => {
        if (match.params.sectionId && match.params.roleId && needUpdate) {
          this.loadUsers(match.params.sectionId, match.params.roleId)
            .then(({ users, total }) =>
              this.setState({ roles, users, totalUsers: total, loading: false, dataLoading: false }));
        } else {
          this.setState({ roles, users: [], totalUsers: 0, loading: false, dataLoading: false }); // eslint-disable-line
        }
      });
    }
  }

  loadSectionsRoles = () => {
    const { match } = this.props;
    return new Promise(resolve =>
      this.loadSections().then((sections) => {
        const roles = this.getRoles(sections);
        if (match.params.sectionId && match.params.roleId) {
          return this.loadUsers(match.params.sectionId, match.params.roleId)
            .then(({ users, total }) => {
              this.setState({
                sections,
                roles,
                users,
                totalUsers: total,
                loading: false,
                dataLoading: false,
              });
              return resolve();
            });
        }
        this.setState({ sections, roles, users: [], totalUsers: 0, loading: false, dataLoading: false });
        return resolve();
      }),
    );
  };

  loadSections = () => {
    const { isSupervisor } = this.props;
    return getSectionList(isSupervisor)
      .then(data => Promise.resolve(data));
  };

  loadUsers = (sectionId, roleId) => {
    const query = getLocationQuery(location);
    const querySearchString = query.search || '';
    return getIntersectionList(sectionId, roleId, querySearchString)
      .then((data) => {
        const users = data.payload;
        return Promise.resolve({ users, total: data.total });
      });
  };

  addUsers = (selectedUsers) => {
    const { sectionId, roleId } = this.props.match.params;
    const body = {
      sectionId,
      roleId,
      addUserArray: selectedUsers.join(','),
    };

    return saveIntersection(body)
      .then(() =>
        this.loadUsers(sectionId, roleId)
          .then(({ users, total }) => {
            this.setState({ users, totalUsers: total });
            return Promise.resolve();
          }),
      );
  };

  addGroup = (selectedGroups) => {
    const { match: { params: { sectionId, roleId } } } = this.props;
    const body = {
      sectionId,
      roleId,
      groupIds: selectedGroups.join(','),
      type: 'addUserArray',
    };

    return saveIntersection(body)
      .then(() =>
        this.loadUsers(sectionId, roleId)
          .then(({ users, total }) => {
            this.setState({ users, totalUsers: total });
            return Promise.resolve();
          }),
      );
  }

  removeAccess = (selectedUsers) => {
    const { sectionId, roleId } = this.props.match.params;
    const body = {
      sectionId,
      roleId,
      deleteUserArray: selectedUsers.join(','),
    };
    return saveIntersection(body)
      .then(() => this.loadUsers(sectionId, roleId)
        .then(({ users, total }) => {
          this.setState({ users, totalUsers: total });
          return Promise.resolve();
        }),
      );
  };

  getRoles = (sections) => {
    const { match } = this.props;
    let roles = [];
    if (match.params.sectionId) {
      const selectedSection = sections.find(section => section.id === match.params.sectionId);
      roles = selectedSection && selectedSection.roleArray ? selectedSection.roleArray : [];
    }
    return roles;
  };

  updateRoles = (roles) => {
    const { match } = this.props;
    const updatedSections = this.state.sections.map((section) => {
      if (section.id === match.params.sectionId) {
        return { ...section, roleArray: roles };
      }
      return section;
    });
    this.setState({ sections: updatedSections, roles });
  };

  getRoleName = () => {
    const { roles } = this.state;
    const { match } = this.props;
    const selectedRole = roles.find(role => role.id === match.params.roleId);
    if (!selectedRole) return '';
    return selectedRole.name;
  };

  getSectionPath = () => {
    const { sections } = this.state;
    const { match } = this.props;
    const section = sections.find(item => item.id === match.params.sectionId);
    if (!section) return '';
    let sectionName = section.name;
    let currentSection = section;
    let parent = sections.find(item => item.id === currentSection.parentSectionId);
    while (parent) {
      currentSection = parent;
      parent = sections.find(item => item.id === currentSection.parentSectionId); // eslint-disable-line
      sectionName = `${currentSection.name} -> ${sectionName}`;
    }
    return sectionName;
  };

  onRoleSelect = roleId =>
    this.props.history.push(routeGenerator.sectionLink(this.props.match.params.sectionId, roleId));

  handleUserListChange = (field, value) => this.setState({ [field]: value });

  render() {
    const { roles, sections, users, totalUsers, loading, dataLoading, search, page } = this.state;
    const sectionId = this.props.match.params.sectionId;
    const roleId = this.props.match.params.roleId;
    const selectedSection = sections.find(sec => sec.id === sectionId);
    const selectedRole = roles.find(role => role.id === roleId);
    if (dataLoading) {
      return (
        <Loader />
      );
    }

    return (
      <div className={s.container}>
        <Sidebar data={sections} onLoadData={this.loadSectionsRoles} />
        <Roles
          roles={roles}
          section={selectedSection}
          onRoleSelect={this.onRoleSelect}
          updateRoles={this.updateRoles}
        />
        <Users
          users={users}
          total={totalUsers}
          section={selectedSection}
          role={selectedRole}
          search={search}
          page={page}
          onChange={this.handleUserListChange}
          sectionPath={this.getSectionPath()}
          onAddUsers={this.addUsers}
          onAddGroups={this.addGroup}
          onRemoveUsers={this.removeAccess}
          roleName={this.getRoleName()}
          loading={loading}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Root/pages/Sections/index.js